import type { KeyboardEvent } from "react";

import type { components } from "../../api/schema";
import { cn } from "../../lib/cn";
import { deriveSnippet, deriveTitle } from "../../lib/journal";
import { formatDateTime } from "../../lib/date";
import { Button } from "../ui/button";

import { FavoriteStar } from "./FavoriteStar";
import { NoteBadge } from "./NoteBadge";

type NoteResponse = components["schemas"]["NoteResponse"];

interface NoteEntryProps {
  note: NoteResponse;
  tagName: (tagId: string) => string;
  // Resolved "SYMBOL · date · P&L" label for a trade note when its trade is in
  // the loaded window (ADR 0036 D7); null falls back to the short trade id.
  tradeLabel: string | null;
  selected: boolean;
  isMutating: boolean;
  onOpen: (note: NoteResponse) => void;
  onToggleFavorite: (note: NoteResponse) => void;
  onRestore: (note: NoteResponse) => void;
}

// One journal entry rendered as the WickScope ".tl-note" card. Three visual
// variants map to note_type: the gold "day note", the dashed freeform "other"
// note, and the solid surface "trade" note linked to a trade.
//
// Per ADR 0036 D8 the explicit Edit / Delete buttons are gone: a live card is a
// click target that opens the row-click side panel (edit happens there). The
// favorite star and the deleted-card Restore button stop propagation so they
// don't also open the panel.
export function NoteEntry({
  note,
  tagName,
  tradeLabel,
  selected,
  isMutating,
  onOpen,
  onToggleFavorite,
  onRestore,
}: NoteEntryProps) {
  const title = deriveTitle(note.body);
  const snippet = deriveSnippet(note.body);
  const openable = !note.is_deleted;

  const linkChip =
    note.note_type === "trade" && note.trade_id !== null ? (
      <span className="rounded-[6px] bg-primary-soft px-2 py-[3px] text-[10px] font-medium text-primary-hover">
        {tradeLabel ?? `Trade ${note.trade_id.slice(0, 8)}`}
      </span>
    ) : note.note_type === "trading_day" && note.trading_day !== null ? (
      <span className="rounded-[6px] bg-surface-container-low px-2 py-[3px] text-[10px] font-medium text-on-surface-variant">
        {note.trading_day}
      </span>
    ) : null;

  function handleOpen(): void {
    if (openable) onOpen(note);
  }
  function handleKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (!openable) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(note);
    }
  }

  return (
    <article
      data-testid={`note-card-${note.note_id}`}
      data-deleted={note.is_deleted ? "true" : undefined}
      aria-current={selected ? "true" : undefined}
      role={openable ? "button" : undefined}
      tabIndex={openable ? 0 : undefined}
      onClick={openable ? handleOpen : undefined}
      onKeyDown={openable ? handleKeyDown : undefined}
      className={cn(
        "rounded-[11px] border p-3.5 transition-[box-shadow,filter]",
        note.note_type === "trading_day" && "border-warn-border bg-warn-soft",
        note.note_type === "other" && "border-dashed border-outline-variant bg-surface-container",
        note.note_type === "trade" && "border-outline-variant bg-surface-container",
        openable && "cursor-pointer hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary",
        selected && "shadow-[inset_3px_0_0_var(--color-primary)]",
        note.is_deleted && "opacity-60",
      )}
    >
      <header className="mb-1.5 flex items-center gap-2">
        <NoteBadge noteType={note.note_type} />
        {linkChip}
        <FavoriteStar
          favorited={note.favorited}
          disabled={isMutating || note.is_deleted}
          onToggle={() => onToggleFavorite(note)}
          stopPropagation
          className="ml-auto"
        />
      </header>

      {title !== "" ? (
        <h3 className="mb-1 text-sm font-semibold text-on-surface">{title}</h3>
      ) : (
        <h3 className="mb-1 text-sm font-semibold italic text-on-surface-muted">
          Untitled note
        </h3>
      )}
      {snippet !== "" ? (
        <p className="mb-2 line-clamp-2 text-xs leading-relaxed text-on-surface-muted">
          {snippet}
        </p>
      ) : null}

      {note.tag_ids.length > 0 ? (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {note.tag_ids.map((tagId) => (
            <li
              key={tagId}
              className="rounded-[9px] border border-outline-variant bg-surface-container-high px-1.5 py-0.5 text-[10px] font-semibold text-on-surface-variant before:text-outline before:content-['#']"
            >
              {tagName(tagId)}
            </li>
          ))}
        </ul>
      ) : null}

      <footer className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10.5px] text-on-surface-muted">
          Updated {formatDateTime(note.updated_at)}
        </span>
        {note.is_deleted ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isMutating}
            onClick={(event) => {
              event.stopPropagation();
              onRestore(note);
            }}
          >
            Restore
          </Button>
        ) : null}
      </footer>
    </article>
  );
}
